import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  Activity, TrendingUp, TrendingDown, Minus, Target, Eye, Shield,
  CheckCircle, Circle, XCircle, Plus, Trash2, Settings, BarChart3,
  ListChecks, Users, RefreshCw, Send, AlertTriangle, ChevronDown, ChevronUp,
  Globe, Search, Zap, ArrowRight
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

const COLOR_MAP = {
  green: { bg: 'bg-green-500/15', text: 'text-green-400', border: 'border-green-500/30', dot: 'bg-green-500' },
  yellow: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', border: 'border-yellow-500/30', dot: 'bg-yellow-500' },
  red: { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30', dot: 'bg-red-500' },
};

const PRIORITY_MAP = {
  HIGH: { bg: 'bg-red-500/15', text: 'text-red-400', label: 'Fix Now' },
  MEDIUM: { bg: 'bg-yellow-500/15', text: 'text-yellow-400', label: 'Watch' },
  LOW: { bg: 'bg-green-500/15', text: 'text-green-400', label: 'Good' },
};

// ==================== MAIN TAB ====================

const SEOTab = () => {
  const [view, setView] = useState('health');
  const [analysis, setAnalysis] = useState(null);
  const [insights, setInsights] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const headers = { headers: getAuth() };
      const [configRes, analysisRes, insightsRes, competitorsRes] = await Promise.all([
        axios.get(API_BASE + '/api/admin/seo/config', headers),
        axios.get(API_BASE + '/api/admin/seo/analyses/latest', headers),
        axios.get(API_BASE + '/api/admin/seo/insights', headers),
        axios.get(API_BASE + '/api/admin/seo/competitors', headers),
      ]);
      setConfig(configRes.data);
      setAnalysis(analysisRes.data);
      setInsights(insightsRes.data);
      setCompetitors(competitorsRes.data);
    } catch (err) {
      console.error('Failed to load SEO data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await axios.post(API_BASE + '/api/admin/seo/run-analysis', {}, { headers: getAuth() });
      setAnalysis(res.data);
      toast.success('SEO analysis complete!');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const views = [
    { id: 'health', icon: Activity, label: 'Health Score' },
    { id: 'actions', icon: ListChecks, label: 'Action List' },
    { id: 'competitors', icon: Users, label: 'Competitor Watch' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  if (loading) return <div className="text-white/50 text-center py-12">Loading SEO data...</div>;

  const needsSetup = !config?.website_url;

  return (
    <div className="space-y-6" data-testid="seo-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">SEO Intelligence</h2>
          <p className="text-white/50 text-sm">
            {analysis ? 'Last analysis: ' + new Date(analysis.run_date).toLocaleDateString() : 'No analysis run yet'}
          </p>
        </div>
        <Button
          onClick={runAnalysis}
          disabled={analyzing || needsSetup}
          className="bg-electric-teal text-primary-dark font-bold"
          data-testid="run-analysis-btn"
        >
          <RefreshCw className={'w-4 h-4 mr-2 ' + (analyzing ? 'animate-spin' : '')} />
          {analyzing ? 'Analyzing...' : 'Run Analysis'}
        </Button>
      </div>

      {/* Setup prompt */}
      {needsSetup && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-400 font-semibold">Setup Required</p>
            <p className="text-white/60 text-sm">Go to Settings and enter your website URL, business name, and target keywords to get started.</p>
          </div>
        </div>
      )}

      {/* View Tabs */}
      <div className="flex gap-1 bg-secondary-dark rounded-lg p-1 border border-white/10">
        {views.map(v => (
          <button
            key={v.id}
            onClick={() => setView(v.id)}
            className={'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ' +
              (view === v.id ? 'bg-electric-teal/20 text-electric-teal' : 'text-white/50 hover:text-white/80')}
            data-testid={'seo-view-' + v.id}
          >
            <v.icon className="w-4 h-4" /> <span className="hidden sm:inline">{v.label}</span>
          </button>
        ))}
      </div>

      {/* Views */}
      {view === 'health' && <HealthView analysis={analysis} />}
      {view === 'actions' && <ActionsView analysis={analysis} insights={insights} onUpdate={loadData} />}
      {view === 'competitors' && <CompetitorView competitors={competitors} analysis={analysis} onUpdate={loadData} />}
      {view === 'settings' && <SettingsView config={config} onUpdate={loadData} />}
    </div>
  );
};


// ==================== HEALTH SCORE VIEW ====================

const HealthView = ({ analysis }) => {
  if (!analysis) return <EmptyState text="Run your first analysis to see your website health score." />;

  const score = analysis.visibility_score || 0;
  const scoreColor = score >= 70 ? '#22c55e' : score >= 40 ? '#eab308' : '#ef4444';
  const trendIcon = analysis.traffic_trend === 'up' ? TrendingUp : analysis.traffic_trend === 'down' ? TrendingDown : Minus;
  const TrendIcon = trendIcon;
  const trendColor = analysis.traffic_trend === 'up' ? 'text-green-400' : analysis.traffic_trend === 'down' ? 'text-red-400' : 'text-yellow-400';

  return (
    <div className="space-y-6" data-testid="health-view">
      {/* Score Gauge */}
      <div className="bg-secondary-dark rounded-lg border border-white/10 p-8 text-center">
        <p className="text-white/40 text-sm uppercase tracking-wider mb-2">Website Health Score</p>
        <div className="relative inline-flex items-center justify-center w-40 h-40 mb-4">
          <svg className="w-40 h-40 -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="50" fill="none" stroke="#333" strokeWidth="10" />
            <circle cx="60" cy="60" r="50" fill="none" stroke={scoreColor} strokeWidth="10"
              strokeDasharray={314} strokeDashoffset={314 - (314 * score / 100)} strokeLinecap="round" />
          </svg>
          <span className="absolute text-4xl font-bold text-white">{score}</span>
        </div>
        <p className="text-white/50 text-sm">out of 100</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          icon={<TrendIcon className={'w-5 h-5 ' + trendColor} />}
          label="Traffic Trend"
          value={analysis.traffic_trend === 'up' ? 'Going Up' : analysis.traffic_trend === 'down' ? 'Going Down' : 'Holding Steady'}
          sub={analysis.traffic_change_pct ? (analysis.traffic_change_pct > 0 ? '+' : '') + analysis.traffic_change_pct + '%' : ''}
          color={trendColor}
        />
        <StatCard
          icon={<Target className="w-5 h-5 text-green-400" />}
          label="Google Page 1"
          value={analysis.page1_keywords + ' keywords'}
          sub="Showing on page 1"
          color="text-green-400"
        />
        <StatCard
          icon={<Eye className="w-5 h-5 text-yellow-400" />}
          label="Almost There"
          value={analysis.page2_3_keywords + ' keywords'}
          sub="Pages 2-3, close to page 1"
          color="text-yellow-400"
        />
      </div>

      {/* Summary Bullets */}
      {analysis.summary_bullets?.length > 0 && (
        <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
          <h3 className="text-white font-bold mb-4">The 30-Second Summary</h3>
          <div className="space-y-3">
            {analysis.summary_bullets.map((bullet, i) => {
              const c = COLOR_MAP[bullet.color] || COLOR_MAP.yellow;
              return (
                <div key={`sb-${bullet.text.slice(0, 15)}-${i}`} className={'flex items-start gap-3 p-3 rounded-lg ' + c.bg}>
                  <div className={'w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ' + c.dot} />
                  <p className={'text-sm ' + c.text}>{bullet.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Competitor Activity */}
      {analysis.competitor_activity && (
        <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
          <h3 className="text-white font-bold mb-2">Competitor Activity</h3>
          <p className="text-white/60 text-sm">{analysis.competitor_activity}</p>
        </div>
      )}
    </div>
  );
};


// ==================== ACTIONS VIEW ====================

const ActionsView = ({ analysis, insights, onUpdate }) => {
  const [expandedAction, setExpandedAction] = useState(null);

  if (!analysis) return <EmptyState text="Run your first analysis to see your action items." />;

  const markDone = async (insightId) => {
    try {
      await axios.put(API_BASE + '/api/admin/seo/insights/' + insightId, { status: 'done' }, { headers: getAuth() });
      toast.success('Marked as done');
      onUpdate();
    } catch (e) { toast.error('Failed to update'); }
  };

  const dismiss = async (insightId) => {
    try {
      await axios.put(API_BASE + '/api/admin/seo/insights/' + insightId, { status: 'dismissed' }, { headers: getAuth() });
      toast.success('Dismissed');
      onUpdate();
    } catch (e) { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-6" data-testid="actions-view">
      {/* Top 3 Actions */}
      {analysis.top_actions?.length > 0 && (
        <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-electric-teal" /> This Week&apos;s Top Priorities
          </h3>
          <div className="space-y-3">
            {analysis.top_actions.map((action, i) => {
              const p = PRIORITY_MAP[action.priority] || PRIORITY_MAP.MEDIUM;
              const isExpanded = expandedAction === i;
              return (
                <div key={`ta-${action.title.slice(0, 15)}-${i}`} className="bg-primary-dark rounded-lg border border-white/10 overflow-hidden">
                  <button
                    onClick={() => setExpandedAction(isExpanded ? null : i)}
                    className="w-full flex items-center gap-3 p-4 text-left"
                  >
                    <span className={'text-lg font-bold w-8 h-8 rounded-full flex items-center justify-center ' + p.bg + ' ' + p.text}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold">{action.title}</p>
                      <span className={'text-xs px-2 py-0.5 rounded ' + p.bg + ' ' + p.text}>{p.label}</span>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-white/10 pt-3">
                      <p className="text-white/70 text-sm">{action.description}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Winning Areas */}
      {analysis.winning_areas?.length > 0 && (
        <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-400" /> Where You&apos;re Winning
          </h3>
          <div className="space-y-2">
            {analysis.winning_areas.map((w, i) => (
              <div key={`wa-${w.keyword}-${i}`} className="flex items-center justify-between p-3 bg-green-500/5 rounded-lg border border-green-500/10">
                <div>
                  <p className="text-white font-medium text-sm">&ldquo;{w.keyword}&rdquo;</p>
                  <p className="text-green-400 text-xs">Position {w.position}</p>
                </div>
                <span className={'text-xs px-2 py-1 rounded ' + (w.trend === 'improving' ? 'bg-green-500/15 text-green-400' : 'bg-yellow-500/15 text-yellow-400')}>
                  {w.trend}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Back Away Areas */}
      {analysis.back_away_areas?.length > 0 && (
        <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-400" /> What To Stop Chasing
          </h3>
          <div className="space-y-2">
            {analysis.back_away_areas.map((b, i) => (
              <div key={`ba-${b.keyword}-${i}`} className="p-3 bg-red-500/5 rounded-lg border border-red-500/10">
                <p className="text-white font-medium text-sm">&ldquo;{b.keyword}&rdquo;</p>
                <p className="text-red-400/70 text-xs mt-1">{b.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Insights Checklist */}
      {insights.length > 0 && (
        <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
          <h3 className="text-white font-bold mb-4">All Action Items</h3>
          <div className="space-y-2">
            {insights.filter(i => i.status !== 'dismissed').map(insight => {
              const c = COLOR_MAP[insight.color] || COLOR_MAP.yellow;
              const isDone = insight.status === 'done';
              return (
                <div key={insight.id} className={'flex items-start gap-3 p-3 rounded-lg border ' + c.border + ' ' + (isDone ? 'opacity-50' : '')} data-testid={'insight-' + insight.id}>
                  <button onClick={() => markDone(insight.id)} className="mt-0.5 flex-shrink-0" data-testid={'mark-done-' + insight.id}>
                    {isDone ? <CheckCircle className="w-5 h-5 text-green-400" /> : <Circle className="w-5 h-5 text-white/30 hover:text-electric-teal" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={'text-xs px-2 py-0.5 rounded ' + c.bg + ' ' + c.text}>{insight.category.replace('_', ' ')}</span>
                      <span className={'text-xs ' + (PRIORITY_MAP[insight.priority]?.text || 'text-white/40')}>{insight.priority}</span>
                    </div>
                    <p className={'text-sm font-medium ' + (isDone ? 'line-through text-white/40' : 'text-white')}>{insight.title}</p>
                    <p className="text-white/50 text-xs mt-1">{insight.description}</p>
                    {insight.action_step && <p className="text-electric-teal/70 text-xs mt-1 flex items-center gap-1"><ArrowRight className="w-3 h-3" /> {insight.action_step}</p>}
                  </div>
                  <button onClick={() => dismiss(insight.id)} className="text-white/20 hover:text-red-400 flex-shrink-0" title="Dismiss">
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


// ==================== COMPETITOR VIEW ====================

const CompetitorView = ({ competitors, analysis, onUpdate }) => {
  const [newName, setNewName] = useState('');
  const [newDomain, setNewDomain] = useState('');

  const addCompetitor = async () => {
    if (!newName || !newDomain) { toast.error('Name and domain required'); return; }
    try {
      await axios.post(API_BASE + '/api/admin/seo/competitors', { competitor_name: newName, domain_url: newDomain }, { headers: getAuth() });
      toast.success('Competitor added');
      setNewName(''); setNewDomain('');
      onUpdate();
    } catch (e) { toast.error('Failed to add'); }
  };

  const removeCompetitor = async (id) => {
    try {
      await axios.delete(API_BASE + '/api/admin/seo/competitors/' + id, { headers: getAuth() });
      toast.success('Removed');
      onUpdate();
    } catch (e) { toast.error('Failed to remove'); }
  };

  return (
    <div className="space-y-6" data-testid="competitors-view">
      {/* Add Competitor */}
      <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
        <h3 className="text-white font-bold mb-4">Track a Competitor</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <input type="text" placeholder="Business name" value={newName} onChange={e => setNewName(e.target.value)}
            className="flex-1 px-3 py-2 bg-primary-dark border border-white/10 rounded-lg text-white placeholder-white/30 focus:border-electric-teal focus:outline-none text-sm"
            data-testid="competitor-name-input" />
          <input type="text" placeholder="website.com" value={newDomain} onChange={e => setNewDomain(e.target.value)}
            className="flex-1 px-3 py-2 bg-primary-dark border border-white/10 rounded-lg text-white placeholder-white/30 focus:border-electric-teal focus:outline-none text-sm"
            data-testid="competitor-domain-input" />
          <Button onClick={addCompetitor} className="bg-electric-teal text-primary-dark font-bold" data-testid="add-competitor-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Competitor List */}
      {competitors.length === 0 ? (
        <EmptyState text="Add competitors to track how you compare in Google search results." />
      ) : (
        <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
          <h3 className="text-white font-bold mb-4">Your Competitors</h3>
          <div className="space-y-3">
            {competitors.map(comp => (
              <div key={comp.id} className="flex items-center justify-between p-4 bg-primary-dark rounded-lg border border-white/10" data-testid={'competitor-' + comp.id}>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-electric-teal" />
                  <div>
                    <p className="text-white font-medium">{comp.competitor_name}</p>
                    <p className="text-white/40 text-sm">{comp.domain_url}</p>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => removeCompetitor(comp.id)}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={'remove-competitor-' + comp.id}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Competitor Activity from Analysis */}
      {analysis?.competitor_activity && (
        <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
          <h3 className="text-white font-bold mb-2">Latest Competitor Intelligence</h3>
          <p className="text-white/60 text-sm">{analysis.competitor_activity}</p>
        </div>
      )}

      {/* Competitor insights from analysis */}
      {analysis?.insights?.filter(i => i.category === 'COMPETITOR_MOVE').length > 0 && (
        <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
          <h3 className="text-white font-bold mb-4">Competitor Moves</h3>
          <div className="space-y-2">
            {analysis.insights.filter(i => i.category === 'COMPETITOR_MOVE').map((ins, i) => (
              <div key={`cm-${ins.title.slice(0, 15)}-${i}`} className="p-3 bg-yellow-500/5 rounded-lg border border-yellow-500/10">
                <p className="text-white font-medium text-sm">{ins.title}</p>
                <p className="text-white/50 text-xs mt-1">{ins.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ==================== SETTINGS FIELD ====================

const SettingsField = ({ label, field, placeholder, type, value, onChange }) => (
  <div>
    <label className="text-white/60 text-xs mb-1 block">{label}</label>
    {type === 'textarea' ? (
      <textarea value={value} onChange={onChange}
        placeholder={placeholder} rows={2}
        className="w-full px-3 py-2 bg-primary-dark border border-white/10 rounded-lg text-white placeholder-white/30 focus:border-electric-teal focus:outline-none text-sm"
        data-testid={'seo-' + field} />
    ) : (
      <input type={type || 'text'} value={value} onChange={onChange}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-primary-dark border border-white/10 rounded-lg text-white placeholder-white/30 focus:border-electric-teal focus:outline-none text-sm"
        data-testid={'seo-' + field} />
    )}
  </div>
);

// ==================== SETTINGS VIEW ====================

const SettingsView = ({ config, onUpdate }) => {
  const [form, setForm] = useState({
    website_url: '', business_name: '', business_type: '', target_location: '',
    core_services: '', target_keywords: '', email_recipient: '', weekly_email_enabled: true,
    serp_api_provider: '', serp_api_key: '', google_ads_customer_id: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      setForm({
        website_url: config.website_url || '',
        business_name: config.business_name || '',
        business_type: config.business_type || '',
        target_location: config.target_location || '',
        core_services: (config.core_services || []).join(', '),
        target_keywords: (config.target_keywords || []).join(', '),
        email_recipient: config.email_recipient || '',
        weekly_email_enabled: config.weekly_email_enabled !== false,
        serp_api_provider: config.serp_api_provider || '',
        serp_api_key: config.serp_api_key || '',
        google_ads_customer_id: config.google_ads_customer_id || '',
      });
    }
  }, [config]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(API_BASE + '/api/admin/seo/config', {
        ...form,
        core_services: form.core_services.split(',').map(s => s.trim()).filter(Boolean),
        target_keywords: form.target_keywords.split(',').map(s => s.trim()).filter(Boolean),
      }, { headers: getAuth() });
      toast.success('Settings saved');
      onUpdate();
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const fieldProps = (field) => ({
    value: form[field],
    onChange: e => setForm(prev => ({ ...prev, [field]: e.target.value })),
    field,
  });

  return (
    <div className="space-y-6" data-testid="settings-view">
      {/* Business Info */}
      <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
        <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Globe className="w-5 h-5 text-electric-teal" /> Business Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SettingsField label="Website URL" field="website_url" placeholder="https://yourbusiness.com" {...fieldProps('website_url')} />
          <SettingsField label="Business Name" field="business_name" placeholder="Your Business Name" {...fieldProps('business_name')} />
          <SettingsField label="Business Type" field="business_type" placeholder="e.g., Electrician, Plumber, Carpenter" {...fieldProps('business_type')} />
          <SettingsField label="Target Location" field="target_location" placeholder="e.g., Victoria, BC" {...fieldProps('target_location')} />
        </div>
        <div className="grid grid-cols-1 gap-4 mt-4">
          <SettingsField label="Core Services (comma separated)" field="core_services" placeholder="EV charger installation, panel upgrades, residential electrical" type="textarea" {...fieldProps('core_services')} />
          <SettingsField label="Target Search Terms (comma separated)" field="target_keywords" placeholder="electrician Victoria, EV charger installation, panel upgrade" type="textarea" {...fieldProps('target_keywords')} />
        </div>
      </div>

      {/* Email Settings */}
      <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
        <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Send className="w-5 h-5 text-electric-teal" /> Weekly Email Report</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SettingsField label="Email Recipient" field="email_recipient" placeholder="owner@business.com" type="email" {...fieldProps('email_recipient')} />
          <div className="flex items-center gap-3 pt-5">
            <button
              onClick={() => setForm(prev => ({ ...prev, weekly_email_enabled: !prev.weekly_email_enabled }))}
              className={'w-12 h-6 rounded-full transition-colors relative ' + (form.weekly_email_enabled ? 'bg-electric-teal' : 'bg-white/20')}
              data-testid="seo-email-toggle"
            >
              <div className={'w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ' + (form.weekly_email_enabled ? 'translate-x-6' : 'translate-x-0.5')} />
            </button>
            <span className="text-white/60 text-sm">Weekly email {form.weekly_email_enabled ? 'enabled' : 'disabled'}</span>
          </div>
        </div>
      </div>

      {/* SERP API (Optional) */}
      <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
        <h3 className="text-white font-bold mb-2 flex items-center gap-2"><Search className="w-5 h-5 text-electric-teal" /> SERP Data Provider <span className="text-xs text-white/30 font-normal">(Optional)</span></h3>
        <p className="text-white/40 text-xs mb-4">Connect a SERP API for real-time Google ranking data. Without this, analysis uses AI estimation.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-white/60 text-xs mb-1 block">Provider</label>
            <select value={form.serp_api_provider} onChange={e => setForm(prev => ({ ...prev, serp_api_provider: e.target.value }))}
              className="w-full px-3 py-2 bg-primary-dark border border-white/10 rounded-lg text-white focus:border-electric-teal focus:outline-none text-sm"
              data-testid="seo-serp_api_provider">
              <option value="">None (AI-only mode)</option>
              <option value="serper">Serper.dev</option>
              <option value="dataforseo">DataForSEO</option>
              <option value="serpapi">SerpAPI</option>
            </select>
          </div>
          <SettingsField label="API Key" field="serp_api_key" placeholder="Enter API key" {...fieldProps('serp_api_key')} />
        </div>
      </div>

      {/* Google Ads Integration */}
      <div className="bg-secondary-dark rounded-lg border border-white/10 p-6">
        <h3 className="text-white font-bold mb-2 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-electric-teal" /> Google Ads Integration
        </h3>
        <p className="text-white/40 text-xs mb-4">Connect Google Ads to automatically adjust campaigns based on weekly SEO findings. Requires a Google Ads account with API access.</p>
        <GoogleAdsConnect />
      </div>

      {/* Save */}
      <Button onClick={save} disabled={saving} className="bg-electric-teal text-primary-dark font-bold w-full sm:w-auto" data-testid="save-seo-settings-btn">
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
};


// ==================== GOOGLE ADS CONNECT ====================

const GoogleAdsConnect = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [devToken, setDevToken] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const res = await axios.get(API_BASE + '/api/admin/seo/google-ads/status', { headers: getAuth() });
        setStatus(res.data);
      } catch (e) { console.error('Failed to load Ads status'); }
      finally { setLoading(false); }
    };
    loadStatus();
  }, []);

  const connect = async () => {
    if (!clientId || !clientSecret || !devToken || !customerId) {
      toast.error('All fields are required');
      return;
    }
    try {
      const res = await axios.post(API_BASE + '/api/admin/seo/google-ads/connect', {
        client_id: clientId, client_secret: clientSecret,
        developer_token: devToken, customer_id: customerId
      }, { headers: getAuth() });
      window.location.href = res.data.auth_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Connection failed'); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Google Ads?')) return;
    try {
      await axios.post(API_BASE + '/api/admin/seo/google-ads/disconnect', {}, { headers: getAuth() });
      setStatus({ connected: false });
      setCampaigns([]);
      toast.success('Disconnected');
    } catch (e) { toast.error('Failed to disconnect'); }
  };

  const loadCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const res = await axios.get(API_BASE + '/api/admin/seo/google-ads/campaigns', { headers: getAuth() });
      setCampaigns(res.data);
    } catch (e) { toast.error('Failed to load campaigns: ' + (e.response?.data?.detail || '')); }
    finally { setLoadingCampaigns(false); }
  };

  const getRecommendations = async () => {
    setLoadingRecs(true);
    try {
      const res = await axios.post(API_BASE + '/api/admin/seo/google-ads/apply-recommendations', {}, { headers: getAuth() });
      setRecommendations(res.data);
      toast.success('Recommendations generated');
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || '')); }
    finally { setLoadingRecs(false); }
  };

  if (loading) return <p className="text-white/40 text-sm">Checking Google Ads status...</p>;

  if (status?.connected) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/20">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <div>
              <p className="text-green-400 font-medium text-sm">Google Ads Connected</p>
              <p className="text-white/40 text-xs">Customer ID: {status.customer_id} | Connected: {new Date(status.connected_at).toLocaleDateString()}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={loadCampaigns} disabled={loadingCampaigns}
              className="border-white/20 text-white/60" data-testid="load-campaigns-btn">
              {loadingCampaigns ? 'Loading...' : 'View Campaigns'}
            </Button>
            <Button size="sm" variant="outline" onClick={disconnect}
              className="border-red-500/30 text-red-400" data-testid="disconnect-ads-btn">
              Disconnect
            </Button>
          </div>
        </div>

        {/* Campaigns Table */}
        {campaigns.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-white/40 text-xs">
                  <th className="text-left p-2">Campaign</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-right p-2">Budget</th>
                  <th className="text-right p-2">Clicks</th>
                  <th className="text-right p-2">Cost</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map(c => (
                  <tr key={c.id} className="border-b border-white/5">
                    <td className="p-2 text-white">{c.name}</td>
                    <td className="p-2"><span className={'text-xs px-2 py-0.5 rounded ' + (c.status === 'ENABLED' ? 'bg-green-500/15 text-green-400' : 'bg-yellow-500/15 text-yellow-400')}>{c.status}</span></td>
                    <td className="p-2 text-right text-white/60">${c.budget?.toFixed(2)}</td>
                    <td className="p-2 text-right text-white/60">{c.clicks}</td>
                    <td className="p-2 text-right text-white/60">${c.cost?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* AI Recommendations */}
        <Button size="sm" onClick={getRecommendations} disabled={loadingRecs}
          className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="get-ads-recs-btn">
          <Zap className="w-3 h-3 mr-1" /> {loadingRecs ? 'Analyzing...' : 'Get AI Ad Recommendations'}
        </Button>

        {recommendations?.recommendations && (
          <div className="space-y-2 mt-3">
            {recommendations.recommendations.map((rec, i) => (
              <div key={`rec-${rec.action}-${rec.keyword || i}`} className="p-3 bg-purple-500/5 rounded-lg border border-purple-500/10">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">{rec.action}</span>
                  {rec.keyword && <span className="text-white/60 text-xs">&ldquo;{rec.keyword}&rdquo;</span>}
                </div>
                <p className="text-white text-sm">{rec.reason}</p>
                {rec.expected_impact && <p className="text-purple-400/70 text-xs mt-1">Expected: {rec.expected_impact}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {!showForm ? (
        <Button size="sm" onClick={() => setShowForm(true)} className="bg-electric-teal text-primary-dark font-bold" data-testid="setup-ads-btn">
          Connect Google Ads Account
        </Button>
      ) : (
        <div className="space-y-3 mt-3">
          <p className="text-white/50 text-xs">You&apos;ll need: a Google Ads developer token, OAuth client ID &amp; secret from Google Cloud Console, and your Google Ads Customer ID.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input type="text" placeholder="OAuth Client ID" value={clientId} onChange={e => setClientId(e.target.value)}
              className="px-3 py-2 bg-primary-dark border border-white/10 rounded-lg text-white placeholder-white/30 focus:border-electric-teal focus:outline-none text-sm" data-testid="ads-client-id" />
            <input type="password" placeholder="OAuth Client Secret" value={clientSecret} onChange={e => setClientSecret(e.target.value)}
              className="px-3 py-2 bg-primary-dark border border-white/10 rounded-lg text-white placeholder-white/30 focus:border-electric-teal focus:outline-none text-sm" data-testid="ads-client-secret" />
            <input type="text" placeholder="Developer Token" value={devToken} onChange={e => setDevToken(e.target.value)}
              className="px-3 py-2 bg-primary-dark border border-white/10 rounded-lg text-white placeholder-white/30 focus:border-electric-teal focus:outline-none text-sm" data-testid="ads-dev-token" />
            <input type="text" placeholder="Customer ID (123-456-7890)" value={customerId} onChange={e => setCustomerId(e.target.value)}
              className="px-3 py-2 bg-primary-dark border border-white/10 rounded-lg text-white placeholder-white/30 focus:border-electric-teal focus:outline-none text-sm" data-testid="ads-customer-id" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={connect} className="bg-electric-teal text-primary-dark font-bold" data-testid="connect-ads-btn">
              Connect & Authorize
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)} className="border-white/20 text-white/60">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
};


// ==================== SHARED COMPONENTS ====================

const StatCard = ({ icon, label, value, sub, color }) => (
  <div className="bg-secondary-dark rounded-lg border border-white/10 p-5">
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <span className="text-white/40 text-xs uppercase tracking-wider">{label}</span>
    </div>
    <p className={'text-xl font-bold ' + (color || 'text-white')}>{value}</p>
    {sub && <p className="text-white/40 text-xs mt-1">{sub}</p>}
  </div>
);

const EmptyState = ({ text }) => (
  <div className="bg-secondary-dark rounded-lg border border-white/10 p-12 text-center">
    <Activity className="w-10 h-10 text-white/20 mx-auto mb-3" />
    <p className="text-white/40">{text}</p>
  </div>
);

export default SEOTab;
